import { NotificationType, useNotification } from '@helpers/use-notification';
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from './services/auth.service';
import { CustomService } from './services/custom.service';
import { LanguageService } from './services/language.service';

const { open } = useNotification();

export interface ErrorMessage {
  error: string;
  message: string;
  path: string;
  status: string;
}

interface JWTToken {
  token: string;
  refreshToken: string;
}

let refreshTokenInProgress = false;
// Refresh Token Subject tracks the current token, or is null if no token is currently
// available (e.g.  refresh pending).
export const refreshToken$: BehaviorSubject<
  string | null
> = new BehaviorSubject<string | null>(null);

export const axiosRequestConfig: AxiosRequestConfig = {
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language': LanguageService.getLanguage(),
  },
  proxy: false,
};

export const instance: AxiosInstance = axios.create(axiosRequestConfig);

instance.interceptors.request.use(
  (config) => {
    const jwtToken = AuthService.getToken();
    const roleId = AuthService.getRoleId();
    const menuId = AuthService.getSubMenuId();
    if (jwtToken) {
      config.headers['Authorization'] = `Bearer ${jwtToken}`;
      if (
        ![`${process.env.REACT_APP_AUTH0_DOMAIN}/oauth/token`].includes(
          config.url || ''
        )
      ) {
        config.headers['x-role-id'] = `${roleId}`;
        config.headers['x-menu-id'] = `${menuId}`;
      }
    }
    return config;
  },
  (error) => {
    console.error('error ', error);
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response: AxiosResponse) => {
    const {
      config: { method = '', url = '' },
    } = response;
    const tokenUrl = [
      '/refreshToken',
      `${process.env.REACT_APP_AUTH0_DOMAIN}/oauth/token`,
    ];
    if (tokenUrl.includes(url)) return response;
    handleNotifyMessage(method, url, NotificationType.SUCCESS);
    return response;
  },
  (error: AxiosError<ErrorMessage>) => {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    const { response, config } = error;
    const { method = '', url = '' } = config;
    const status = response ? response.status : 0;
    if (!method) return Promise.reject(error);

    if ([401, 403].includes(status) && url === '/refreshToken') {
      window.location.replace(process.env.REACT_APP_SETDD_URL || '');
    }
    if (status === 401) {
      return new Promise((resolve) => {
        setRefreshToken(error).subscribe({
          next: () => {
            resolve(instance(error.config));
          },
        });
      });
    }
    if (isScreenNotYours(status, response?.data?.message)) {
      // Send them home instead of leaving a rendered screen with an error toast on it.
      //
      // withMenuAccess already covers the case it can see: the path is absent from the cached
      // sidebar, checked before render. It cannot cover this one - the page renders because the
      // path IS in the sidebar, and only the server knows the menu is no longer usable: a stale
      // x-menu-id in an old tab, a menu revoked while the sidebar is still cached, or a route
      // that carries no wrapper. Reacting to the refusal is the only thing that catches those.
      //
      // Deliberately no toast: replace() reloads the page, so a toast would be discarded unseen.
      if (window.location.pathname !== '/') window.location.replace('/');
      return Promise.reject(error);
    }
    handleNotifyMessage(
      method,
      url,
      NotificationType.ERROR,
      response?.data?.message,
      config.responseType === 'blob'
    );
    return Promise.reject(error);
  }
);

/**
 * Whether a refusal means "this screen was never yours", as opposed to "that action failed".
 *
 * The distinction has to be exact, because the API answers 400 for every IllegalArgumentException -
 * "Cannot send report group status Sent", "Not found broker report id 123", "Not allow user to send
 * report group". Those are failed actions on a screen the user legitimately has, and yanking them
 * home mid-task would be worse than the toast they get today.
 *
 * Only two refusals are about the screen itself:
 *   403                              - the API's @PreAuthorize refused the whole controller
 *   400 "User cannot access this menu" - the caller holds no role granting the menu they sent
 *
 * Second of two layers. withMenuAccess (@helpers/with-menu-access) catches what it can see before
 * render - the path is absent from the cached sidebar - and so avoids the empty shell. This catches
 * what the sidebar cannot know: a menu revoked mid-session, a stale x-menu-id, an unwrapped route.
 */
export const MENU_DENIED_MESSAGE = 'User cannot access this menu';

export const isScreenNotYours = (status: number, message?: string): boolean =>
  status === 403 || (status === 400 && message === MENU_DENIED_MESSAGE);

const handleNotifyMessage = (
  method: string,
  url: string,
  type: NotificationType,
  message?: string,
  isDownload = false
) => {
  const extMsg =
    type === NotificationType.SUCCESS ? 'Successfully' : 'Failed!!';
  if (method === 'get') {
    // A failed GET used to surface nothing at all: a rejected list left the table showing
    // "No Data", indistinguishable from an empty result, and a rejected detail opened an
    // empty form. Only on error - a successful GET must stay silent.
    // Downloads are excluded: those callers already raise their own toast, and their blob
    // response body cannot carry a parsed message anyway.
    if (type === NotificationType.ERROR && !isDownload) {
      open({ type, description: message || `Load ${extMsg}` });
    }
    return;
  }
  if (['post', 'put'].includes(method || '')) {
    const isUrlCopyToDraft = /^((\/broker-report)(-group)?(\/sent\/)([0-9]*)\/copy)/.test(
      url
    );
    const isUrlSent = /^((\/broker-report)(-group)?(\/draft\/)([0-9]*)\/sent)/.test(
      url
    );
    if (isUrlCopyToDraft) {
      open({ type, description: `Copy ${extMsg}` });
    } else if (isUrlSent) {
      open({ type, description: `Sent ${extMsg}` });
    } else {
      open({ type, description: !message ? `Save ${extMsg}` : message });
    }
  } else if (method === 'delete') {
    open({ type, description: `Delete ${extMsg}` });
  }
};

const setRefreshToken = (error: AxiosError) => {
  if (refreshTokenInProgress) {
    return refreshToken$.pipe(
      filter((result) => result !== null),
      take(1),
      switchMap(() => of(refreshToken$.getValue()))
    );
  } else {
    refreshTokenInProgress = true;
    // Set the refreshTokenSubject to null so that subsequent API calls will wait until the new token has been retrieved
    refreshToken$.next(null);
    AuthService.removeToken();
    return getRefreshToken(error.config).pipe(
      switchMap((response: JWTToken) => {
        // When the call to refreshToken completes we reset the refreshTokenInProgress to false
        // for the next time the token needs to be refreshed
        const { token, refreshToken } = response;
        AuthService.setToken(token);
        AuthService.setRefreshToken(refreshToken);
        refreshTokenInProgress = false;
        refreshToken$.next(token);
        return of(token);
      })
    );
  }
};

const getRefreshToken = (config: AxiosRequestConfig): Observable<JWTToken> => {
  const refreshToken = AuthService.getRefreshToken();
  const roleId = AuthService.getRoleId();
  const menuId = AuthService.getSubMenuId();
  config.headers['Authorization'] = `Bearer ${refreshToken}`;
  config.headers['x-role-id'] = `${roleId}`;
  config.headers['x-menu-id'] = `${menuId}`;
  return CustomService.createData('/refreshToken', null, config.headers);
};

export default instance;
